/**
 * AdminIonAnalyticsSubtab — anonymized Ion AI usage analytics.
 *
 * Answers two questions WITHOUT attributing anything to a specific user
 * (legal basis: TOS §14(1) aggregated/anonymized usage analytics):
 *   (a) How much do users talk to Ion? -> volume time series + engagement
 *       histogram (conversations per active user).
 *   (b) What kinds of conversations happen? -> type breakdown, derived on
 *       the backend strictly from tool/flow metadata (never message text).
 *   (c) What TOPICS do people ask about? -> topic breakdown, derived from
 *       curated knowledge-base topic_ids and cell-selection branches — a
 *       finer grain than (b); a conversation can appear under several
 *       topics (unlike types, which picks one dominant label).
 *
 * K-anonymity (k=5) is enforced server-side
 * (`app.api.v1.chat.repository._suppress_small_buckets` /
 * `_K_ANON_THRESHOLD`) — any bucket backed by fewer than 5 distinct users
 * arrives here already redacted (`suppressed: true` / null counts on the
 * volume series, or folded into a `"(suppressed)"` row on types/engagement).
 * This component only has to render that honestly, never re-derive it.
 *
 * Mirrors AdminIonFeedbackSubtab.jsx for subtab structure and
 * analytics/AnalyticsAiUsageView.jsx for ECharts conventions (chartTheme,
 * COLORS, KPICard, formatNumber).
 */
import * as echarts from 'echarts';
import { useEffect, useRef, useState } from 'preact/hooks';
import { authenticatedFetch } from '../../services/authService.js';
import { COLORS, chartTheme } from './analytics/shared/chartTheme.js';
import { formatNumber, getApiBase } from './analytics/shared/formatters.js';
import { KPICard } from './analytics/shared/KPICard.jsx';
import AdminIonUsageReports from './AdminIonUsageReports.jsx';

const SUPPRESSED = '(suppressed)';

const TYPE_LABELS = {
  'cell-selection': 'Cell selection',
  benchmarking: 'Benchmarking',
  plotting: 'Plotting',
  'knowledge-qa': 'Knowledge Q&A',
  'web-search': 'Web search',
  'data-question': 'Data question',
  other: 'Other',
  'general-chat': 'General chat',
  [SUPPRESSED]: 'Low-volume (hidden, k<5)',
};

const ENGAGEMENT_LABELS = {
  '1': '1 conversation',
  '2-5': '2–5 conversations',
  '6-10': '6–10 conversations',
  '11+': '11+ conversations',
  [SUPPRESSED]: 'Low-volume (hidden, k<5)',
};

function typeLabel(t) {
  return TYPE_LABELS[t] || t;
}

function engagementLabel(b) {
  return ENGAGEMENT_LABELS[b] || b;
}

function topicLabel(row) {
  return row.topic === SUPPRESSED ? 'Low-volume (hidden, k<5)' : row.label || row.topic;
}

// --------------------------------------------------------------------------
// Volume time series
// --------------------------------------------------------------------------

function VolumeChart({ points }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !points?.length) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }

    const dates = points.map((p) => (p.bucket || '').slice(0, 10));
    const conversations = points.map((p) => (p.suppressed ? null : p.conversations));
    const users = points.map((p) => (p.suppressed ? null : p.distinct_users));

    chartInstance.current.setOption({
      ...chartTheme,
      grid: { left: 55, right: 55, top: 40, bottom: 40 },
      tooltip: {
        ...chartTheme.tooltip,
        trigger: 'axis',
        formatter: (params) => {
          const idx = params[0]?.dataIndex ?? 0;
          const p = points[idx];
          if (p?.suppressed) {
            return `${dates[idx]}<br/>Hidden — fewer than 5 distinct users that day (k-anonymity)`;
          }
          return params
            .map((s) => `${s.marker} ${s.seriesName}: ${s.value ?? '—'}`)
            .join('<br/>');
        },
      },
      legend: { ...chartTheme.legend, top: 0, right: 0, data: ['Conversations', 'Distinct users'] },
      xAxis: {
        type: 'category', data: dates,
        axisLine: { lineStyle: { color: COLORS.grid } },
        axisLabel: { color: COLORS.gray, fontSize: 10, rotate: 45 },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      series: [
        {
          name: 'Conversations', type: 'bar', data: conversations,
          itemStyle: { color: COLORS.orange + 'cc' },
        },
        {
          name: 'Distinct users', type: 'line', data: users,
          smooth: true, itemStyle: { color: COLORS.cyan }, lineStyle: { width: 2 },
          connectNulls: false,
        },
      ],
    });

    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [points]);

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null; }, []);

  if (!points || points.length === 0) {
    return <div className="empty-state">No Ion activity in this period.</div>;
  }
  const anySuppressed = points.some((p) => p.suppressed);
  return (
    <>
      <div ref={chartRef} style={{ width: '100%', height: 300 }} />
      {anySuppressed && (
        <p className="admin-aim-field-hint" style={{ marginTop: 4 }}>
          Days with a gap in the lines had fewer than 5 distinct users active — counts
          are hidden (k-anonymity) to prevent fingerprinting a single customer.
        </p>
      )}
    </>
  );
}

// --------------------------------------------------------------------------
// Conversation-type breakdown
// --------------------------------------------------------------------------

function TypeBreakdownChart({ rows }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !rows?.length) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }
    const sorted = [...rows].sort((a, b) => b.conversations - a.conversations);
    chartInstance.current.setOption({
      ...chartTheme,
      tooltip: {
        ...chartTheme.tooltip,
        trigger: 'item',
        formatter: (p) => `${p.name}<br/>${p.value} conversations`,
      },
      legend: { ...chartTheme.legend, orient: 'vertical', left: 10, top: 'middle' },
      series: [{
        name: 'Conversation type', type: 'pie', radius: ['40%', '70%'], center: ['65%', '50%'],
        data: sorted.map((r) => ({
          value: r.conversations,
          name: typeLabel(r.type),
          itemStyle: r.type === SUPPRESSED ? { color: COLORS.gray } : undefined,
        })),
        emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.5)' } },
        label: { color: COLORS.gray },
      }],
      color: [COLORS.orange, COLORS.cyan, COLORS.green, COLORS.purple, COLORS.orangeLight, COLORS.cyanLight, COLORS.red, COLORS.gray],
    });
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [rows]);

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null; }, []);

  if (!rows || rows.length === 0) return <div className="empty-state">No data yet.</div>;
  return <div ref={chartRef} style={{ width: '100%', height: 280 }} />;
}

function TypeBreakdownTable({ rows }) {
  if (!rows || rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) => b.conversations - a.conversations);
  return (
    <table className="analytics-table" style={{ width: '100%', fontSize: 13, marginTop: 12 }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left' }}>Type</th>
          <th>Conversations</th>
          <th>Distinct users</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => (
          <tr key={r.type}>
            <td style={{ textAlign: 'left', color: r.type === SUPPRESSED ? COLORS.gray : undefined }}>
              {typeLabel(r.type)}
            </td>
            <td style={{ textAlign: 'right' }}>{formatNumber(r.conversations)}</td>
            <td style={{ textAlign: 'right' }}>{formatNumber(r.distinct_users)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --------------------------------------------------------------------------
// Topic breakdown (finer grain than conversation type)
// --------------------------------------------------------------------------

function TopicsChart({ rows }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !rows?.length) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }
    const sorted = [...rows].sort((a, b) => b.conversations - a.conversations).slice(0, 15);
    const labels = sorted.map(topicLabel).reverse();
    const values = sorted.map((r) => r.conversations).reverse();
    const isSuppressed = sorted.map((r) => r.topic === SUPPRESSED).reverse();

    chartInstance.current.setOption({
      ...chartTheme,
      grid: { left: 160, right: 30, top: 10, bottom: 30 },
      tooltip: {
        ...chartTheme.tooltip,
        trigger: 'axis',
        formatter: (params) => {
          const p = params[0];
          return `${p.name}<br/>${p.value} conversations`;
        },
      },
      xAxis: {
        type: 'value',
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      yAxis: {
        type: 'category', data: labels,
        axisLabel: { color: COLORS.gray, fontSize: 11 },
      },
      series: [{
        name: 'Conversations', type: 'bar',
        data: values,
        itemStyle: {
          color: (p) => (isSuppressed[p.dataIndex] ? COLORS.gray : COLORS.orange),
        },
      }],
    });
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [rows]);

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null; }, []);

  if (!rows || rows.length === 0) return <div className="empty-state">No topic data yet.</div>;
  return <div ref={chartRef} style={{ width: '100%', height: Math.max(220, Math.min(rows.length, 15) * 28) }} />;
}

function TopicsTable({ rows }) {
  if (!rows || rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) => b.conversations - a.conversations);
  return (
    <table className="analytics-table" style={{ width: '100%', fontSize: 13, marginTop: 12 }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left' }}>Topic</th>
          <th>Conversations</th>
          <th>Distinct users</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => (
          <tr key={r.topic}>
            <td style={{ textAlign: 'left', color: r.topic === SUPPRESSED ? COLORS.gray : undefined }}>
              {topicLabel(r)}
            </td>
            <td style={{ textAlign: 'right' }}>{formatNumber(r.conversations)}</td>
            <td style={{ textAlign: 'right' }}>{formatNumber(r.distinct_users)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --------------------------------------------------------------------------
// Engagement histogram
// --------------------------------------------------------------------------

const ENGAGEMENT_ORDER = ['1', '2-5', '6-10', '11+', SUPPRESSED];

function EngagementChart({ buckets }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !buckets?.length) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }
    const byBucket = new Map(buckets.map((b) => [b.bucket, b.users]));
    const order = ENGAGEMENT_ORDER.filter((b) => byBucket.has(b));
    chartInstance.current.setOption({
      ...chartTheme,
      grid: { left: 55, right: 20, top: 20, bottom: 60 },
      tooltip: { ...chartTheme.tooltip, trigger: 'axis' },
      xAxis: {
        type: 'category', data: order.map(engagementLabel),
        axisLabel: { color: COLORS.gray, fontSize: 11, rotate: 20 },
      },
      yAxis: {
        type: 'value', name: 'Users',
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      series: [{
        name: 'Users', type: 'bar',
        data: order.map((b) => byBucket.get(b) ?? 0),
        itemStyle: {
          color: (p) => (order[p.dataIndex] === SUPPRESSED ? COLORS.gray : COLORS.cyan),
        },
      }],
    });
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [buckets]);

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null; }, []);

  if (!buckets || buckets.length === 0) return <div className="empty-state">No data yet.</div>;
  return <div ref={chartRef} style={{ width: '100%', height: 260 }} />;
}

// --------------------------------------------------------------------------
// Main subtab
// --------------------------------------------------------------------------

export function AdminIonAnalyticsSubtab() {
  const [rangeDays, setRangeDays] = useState(30);
  const [volume, setVolume] = useState(null);
  const [types, setTypes] = useState(null);
  const [topics, setTopics] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    const base = getApiBase();
    const bucket = rangeDays <= 2 ? 'hour' : 'day';
    try {
      const [vRes, tRes, topicRes, eRes] = await Promise.all([
        authenticatedFetch(`${base}/admin/ion-analytics/volume?range_days=${rangeDays}&bucket=${bucket}`),
        authenticatedFetch(`${base}/admin/ion-analytics/types?range_days=${rangeDays}`),
        authenticatedFetch(`${base}/admin/ion-analytics/topics?range_days=${rangeDays}`),
        authenticatedFetch(`${base}/admin/ion-analytics/engagement?range_days=${rangeDays}`),
      ]);
      if (vRes.ok) setVolume(await vRes.json());
      if (tRes.ok) setTypes(await tRes.json());
      if (topicRes.ok) setTopics(await topicRes.json());
      if (eRes.ok) setEngagement(await eRes.json());
      if (!vRes.ok && !tRes.ok && !topicRes.ok && !eRes.ok) setError('Failed to load Ion analytics.');
    } catch (e) {
      setError(e?.message || 'network');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDays]);

  const totalConversations = (types?.types || []).reduce((s, r) => s + (r.conversations || 0), 0);
  const totalUsers = Math.max(0, ...(types?.types || []).map((r) => r.distinct_users || 0));
  const visiblePoints = (volume?.points || []).filter((p) => !p.suppressed);
  const totalMessages = visiblePoints.reduce((s, p) => s + (p.messages || 0), 0);

  return (
    <div className="admin-aim-subtab-body">
      <header className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title">Ion Usage Analytics</h2>
          <p className="admin-aim-sub">
            Anonymized usage analytics (TOS §14(1)) — no data here is attributable to a
            specific user. Conversation type is derived only from tool/flow metadata,
            never message content. Any bucket with fewer than 5 distinct users is
            suppressed (k-anonymity).
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            className="analytics-period-select"
            value={rangeDays}
            onChange={(e) => setRangeDays(parseInt(e.target.value, 10))}
          >
            <option value="7">Last 7 Days</option>
            <option value="14">Last 14 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
          </select>
          <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={fetchAll}>↻</button>
        </div>
      </header>

      {error && <div className="admin-aim-error-banner">{error}</div>}

      <div className="analytics-kpi-grid" style={{ marginBottom: 16 }}>
        <KPICard
          icon={'💬'}
          label="Conversations"
          value={formatNumber(totalConversations)}
          sublabel={`${formatNumber(totalMessages)} messages`}
          color="orange"
        />
        <KPICard
          icon={'👥'}
          label="Distinct users (peak type)"
          value={formatNumber(totalUsers)}
          sublabel="Largest single-type user count — never a per-user total"
          color="cyan"
        />
      </div>

      <section style={{ marginBottom: 24 }}>
        <h3 className="section-title">Volume over time</h3>
        {loading && !volume ? (
          <div className="analytics-loading"><div className="spinner" /></div>
        ) : (
          <VolumeChart points={volume?.points} />
        )}
      </section>

      <section style={{ marginBottom: 24 }}>
        <h3 className="section-title">Conversation types</h3>
        <p className="admin-aim-field-hint" style={{ marginTop: -4, marginBottom: 8 }}>
          Derived from tool calls (plotting, cell-selection, benchmarking, …) and
          cell-selection workflow telemetry — never from reading chat text.
        </p>
        {loading && !types ? (
          <div className="analytics-loading"><div className="spinner" /></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <TypeBreakdownChart rows={types?.types} />
            <TypeBreakdownTable rows={types?.types} />
          </div>
        )}
      </section>

      <section style={{ marginBottom: 24 }}>
        <h3 className="section-title">Topics</h3>
        <p className="admin-aim-field-hint" style={{ marginTop: -4, marginBottom: 8 }}>
          Derived from curated knowledge-base topic_ids (the AI copies these verbatim
          from its own knowledge index — never free text) and cell-selection application
          branches. A conversation can appear under several topics.
        </p>
        {loading && !topics ? (
          <div className="analytics-loading"><div className="spinner" /></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <TopicsChart rows={topics?.topics} />
            <TopicsTable rows={topics?.topics} />
          </div>
        )}
      </section>

      <section>
        <h3 className="section-title">Engagement — conversations per active user</h3>
        {loading && !engagement ? (
          <div className="analytics-loading"><div className="spinner" /></div>
        ) : (
          <EngagementChart buckets={engagement?.buckets} />
        )}
      </section>

      <AdminIonUsageReports />
    </div>
  );
}
